import { ShieldCheck, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfidentialityData, isSensitivePackage } from "@/domain/intake";
import { cn } from "@/lib/utils";

interface ConfidentialityStepProps {
  data: ConfidentialityData;
  onChange: (data: ConfidentialityData) => void;
  onNext: () => void;
  onBack: () => void;
  selectedPackageId: string | null;
}

export function ConfidentialityStep({
  data,
  onChange,
  onNext,
  onBack,
  selectedPackageId,
}: ConfidentialityStepProps) {
  const isSensitive = isSensitivePackage(selectedPackageId);

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold mb-2">
          Confidentiality
        </h2>
        <p className="text-muted-foreground mb-6">
          We take data security seriously. If your project involves sensitive
          information, we can sign an NDA before the discovery call.
        </p>

        <div className="space-y-6">
          <div
            className={cn(
              "p-4 rounded-lg border transition-all",
              data.ndaRequested
                ? "border-primary bg-primary/5"
                : "border-border"
            )}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="ndaToggle"
                checked={data.ndaRequested}
                onCheckedChange={(checked) =>
                  onChange({ ...data, ndaRequested: checked === true })
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="ndaToggle"
                  className="text-base font-medium cursor-pointer"
                >
                  Request an NDA before sharing sensitive details
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSensitive
                    ? "Recommended for MCP Gateway and Knowledge Engine projects that involve proprietary data."
                    : "Optional — recommended if your project involves proprietary data, credentials, or customer information."}
                </p>
              </div>
              <ShieldCheck
                className={cn(
                  "h-5 w-5 shrink-0 mt-0.5",
                  data.ndaRequested ? "text-primary" : "text-muted-foreground"
                )}
              />
            </div>
          </div>

          <div
            className={cn(
              "p-4 rounded-lg border transition-all",
              data.willShareSensitiveData
                ? "border-amber-500/50 bg-amber-500/5"
                : "border-border"
            )}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="sensitiveData"
                checked={data.willShareSensitiveData}
                onCheckedChange={(checked) =>
                  onChange({
                    ...data,
                    willShareSensitiveData: checked === true,
                  })
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="sensitiveData"
                  className="text-base font-medium cursor-pointer"
                >
                  Will you share credentials, proprietary datasets, or customer
                  data before the call?
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  If yes, an NDA will be required before we can schedule the
                  discovery call.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-300">
                  What&apos;s safe to share without an NDA
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  <li>High-level project goals and use cases</li>
                  <li>Public information about your company</li>
                  <li>General technology stack preferences</li>
                  <li>Timeline and budget expectations</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="safeSummary">
              What can you share now? (optional)
            </Label>
            <Textarea
              id="safeSummary"
              placeholder="Share any non-sensitive project details to help us prepare for the call..."
              rows={3}
              value={data.safeSummary}
              onChange={(e) =>
                onChange({ ...data, safeSummary: e.target.value })
              }
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {data.safeSummary.length}/1000 characters
            </p>
          </div>

          <div className="pt-4 flex justify-between">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button onClick={onNext} size="lg">
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
